import { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';
import { useMentorContext } from '../contexts/MentorContext';

/**
 * MentorSuggestionsPage — displays the mentor's suggestion history with
 * status badges, detail panel, and empty/error/inactive states.
 *
 * Data:
 *   - Fetches from `GET /api/mentors/suggestions`
 *   - Shows up to 50 suggestions sorted by created_at descending (R8.1)
 *
 * States:
 *   - Loading: skeleton placeholders (R8.1)
 *   - Inactive link: message, no fetch attempt (R8.6)
 *   - Empty: friendly message with navigation links to KB/documents (R8.4)
 *   - Error: message + retry button (R8.5)
 *   - Success: list with detail panel on selection (R8.2)
 *
 * Accessibility:
 *   - Status badges use colour + text label (R8.3)
 *   - Semantic roles and ARIA attributes throughout
 *
 * Requirements: 8.1–8.6
 */

export interface Suggestion {
  pk: string;
  sk: string;
  mentor_id: string;
  target_type: string;
  target_ref: string;
  comment: string;
  status: 'open' | 'accepted' | 'dismissed';
  created_at: string;
}

interface SuggestionsResponse {
  suggestions: Suggestion[];
}

type FetchState =
  | { status: 'loading' }
  | { status: 'success'; suggestions: Suggestion[] }
  | { status: 'error'; message: string };

const MAX_SUGGESTIONS = 50;
const COMMENT_TRUNCATE_LENGTH = 200;

export default function MentorSuggestionsPage() {
  const { dashboard } = useMentorContext();
  const [state, setState] = useState<FetchState>({ status: 'loading' });
  const [selectedSuggestion, setSelectedSuggestion] = useState<Suggestion | null>(null);

  const fetchSuggestions = useCallback(async () => {
    setState({ status: 'loading' });
    setSelectedSuggestion(null);
    try {
      const data = await api.get<SuggestionsResponse>('/api/mentors/suggestions');
      // R8.1: Sort by creation date (newest first), cap at 50
      const sorted = (data.suggestions ?? [])
        .sort((a, b) => b.created_at.localeCompare(a.created_at))
        .slice(0, MAX_SUGGESTIONS);
      setState({ status: 'success', suggestions: sorted });
    } catch {
      setState({
        status: 'error',
        message: 'Suggestions could not be loaded.',
      });
    }
  }, []);

  useEffect(() => {
    // R8.6: If link inactive, don't attempt to fetch
    if (dashboard && dashboard.status === 'active') {
      fetchSuggestions();
    }
  }, [dashboard, fetchSuggestions]);

  // R8.6: Inactive link → show message, don't fetch
  if (dashboard && dashboard.status !== 'active') {
    return (
      <div>
        <h1 className="text-2xl font-bold text-gray-900 mb-4">Suggestions</h1>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4" role="alert">
          <p className="text-amber-800">
            Your mentor link is not currently active. Suggestion history is unavailable.
          </p>
        </div>
      </div>
    );
  }

  // Loading state (also shown while dashboard context is resolving)
  if (state.status === 'loading') {
    return (
      <div aria-label="Loading suggestions">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">Suggestions</h1>
        <div className="animate-pulse space-y-3">
          <div className="h-14 bg-gray-200 rounded w-full" />
          <div className="h-14 bg-gray-200 rounded w-full" />
          <div className="h-14 bg-gray-200 rounded w-full" />
          <div className="h-14 bg-gray-200 rounded w-full" />
        </div>
      </div>
    );
  }

  // R8.5: Error state with retry
  if (state.status === 'error') {
    return (
      <div>
        <h1 className="text-2xl font-bold text-gray-900 mb-4">Suggestions</h1>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4" role="alert">
          <p className="text-red-700">{state.message}</p>
          <button
            onClick={fetchSuggestions}
            className="mt-3 text-sm font-medium text-red-700 hover:text-red-900 underline"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // R8.4: Empty state with links to KB/documents
  if (state.suggestions.length === 0) {
    return (
      <div>
        <h1 className="text-2xl font-bold text-gray-900 mb-4">Suggestions</h1>
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center" role="status">
          <p className="text-gray-600 mb-3">
            You haven't left any suggestions yet.
          </p>
          <p className="text-sm text-gray-500">
            Visit the{' '}
            <a href="/mentor/kb" className="text-blue-600 hover:text-blue-800 underline">
              Knowledge Base
            </a>{' '}
            or{' '}
            <a href="/mentor/documents" className="text-blue-600 hover:text-blue-800 underline">
              Documents
            </a>{' '}
            to leave feedback.
          </p>
        </div>
      </div>
    );
  }

  // R8.2: List with detail panel on selection
  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-4">Suggestions</h1>

      <div className="flex gap-6">
        {/* Left panel — suggestion list */}
        <div className={selectedSuggestion ? 'w-1/2' : 'w-full'}>
          <ul className="space-y-2" aria-label="Suggestion list">
            {state.suggestions.map((suggestion) => (
              <li key={suggestion.sk}>
                <SuggestionRow
                  suggestion={suggestion}
                  isSelected={selectedSuggestion?.sk === suggestion.sk}
                  onSelect={() => setSelectedSuggestion(suggestion)}
                />
              </li>
            ))}
          </ul>
        </div>

        {/* Right panel — full detail on selection */}
        {selectedSuggestion && (
          <div className="w-1/2">
            <SuggestionDetail
              suggestion={selectedSuggestion}
              onClose={() => setSelectedSuggestion(null)}
            />
          </div>
        )}
      </div>
    </div>
  );
}

/** Individual suggestion row in the list (R8.2) */
function SuggestionRow({
  suggestion,
  isSelected,
  onSelect,
}: {
  suggestion: Suggestion;
  isSelected: boolean;
  onSelect: () => void;
}) {
  const truncatedComment =
    suggestion.comment.length > COMMENT_TRUNCATE_LENGTH
      ? suggestion.comment.slice(0, COMMENT_TRUNCATE_LENGTH) + '…'
      : suggestion.comment;

  return (
    <button
      onClick={onSelect}
      className={`w-full text-left px-4 py-3 rounded-lg border transition-colors ${
        isSelected
          ? 'border-blue-300 bg-blue-50'
          : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
      }`}
      aria-pressed={isSelected}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-xs font-medium text-gray-500 mb-1">
            {formatTargetRef(suggestion.target_type, suggestion.target_ref)}
          </p>
          <p className="text-sm text-gray-900">{truncatedComment}</p>
          <p className="text-xs text-gray-500 mt-1">
            {formatDateTime(suggestion.created_at)}
          </p>
        </div>
        <StatusBadge status={suggestion.status} />
      </div>
    </button>
  );
}

/** Full detail panel for a selected suggestion */
function SuggestionDetail({
  suggestion,
  onClose,
}: {
  suggestion: Suggestion;
  onClose: () => void;
}) {
  return (
    <div className="border border-gray-200 rounded-lg bg-white p-5 sticky top-6">
      <div className="flex items-center justify-between mb-4">
        <StatusBadge status={suggestion.status} />
        <button
          onClick={onClose}
          className="text-sm text-gray-500 hover:text-gray-700"
          aria-label="Close detail"
        >
          Close
        </button>
      </div>

      {/* Target reference */}
      <div className="mb-3">
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
          Target
        </h3>
        <p className="text-sm text-gray-700">
          {formatTargetRef(suggestion.target_type, suggestion.target_ref)}
        </p>
      </div>

      {/* Full comment (R8.2) */}
      <div className="mb-3">
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
          Comment
        </h3>
        <p className="text-sm text-gray-800 whitespace-pre-wrap">{suggestion.comment}</p>
      </div>

      {/* Creation date */}
      <div>
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
          Created
        </h3>
        <p className="text-sm text-gray-700">{formatDateTime(suggestion.created_at)}</p>
      </div>
    </div>
  );
}

/**
 * Status badge — combines colour with text label for accessibility (R8.3).
 * Status is discernible without reliance on colour alone.
 */
function StatusBadge({ status }: { status: Suggestion['status'] }) {
  const config = STATUS_CONFIG[status];
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium flex-shrink-0 ${config.classes}`}
    >
      {config.label}
    </span>
  );
}

const STATUS_CONFIG: Record<
  Suggestion['status'],
  { label: string; classes: string }
> = {
  open: { label: 'Open', classes: 'bg-blue-100 text-blue-800' },
  accepted: { label: 'Accepted', classes: 'bg-green-100 text-green-800' },
  dismissed: { label: 'Dismissed', classes: 'bg-gray-100 text-gray-700' },
};

/** Format target_type + target_ref into a human-readable label */
function formatTargetRef(targetType: string, targetRef: string): string {
  const typeLabel = TARGET_TYPE_LABELS[targetType] ?? targetType;
  return `${typeLabel}: ${targetRef}`;
}

const TARGET_TYPE_LABELS: Record<string, string> = {
  kb_entry: 'KB Entry',
  cv_bullet: 'CV Bullet',
  cover_letter: 'Cover Letter',
};

/** Format ISO date string to a locale-appropriate date-time string (R8.2) */
function formatDateTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}
